import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Icone, type NomeIcone } from "@/components/Icone";
import type { AreaKey } from "@/lib/permissoes";
import { euQuery } from "@/web/src/consultas";

/** As partes da Empresa, uma por página. O formulário por baixo continua um só (ver FormEmpresa). */
export const SECOES_EMPRESA = ["clinica", "unidades", "profissionais", "servicos", "avisos", "constituicao"] as const;
export type SecaoEmpresa = (typeof SECOES_EMPRESA)[number];

type Item = { rotulo: string; icone: NomeIcone; area: AreaKey; to: string; secao?: SecaoEmpresa };

/**
 * Tudo que se configura mora num lugar só, em três grupos. Antes eram cinco
 * ícones soltos no menu (Empresa, Modelos, Conexão, Equipe, Configurações) e
 * um formulário de sete seções numa página — quem viu o app achou desorganizado.
 *
 * Cada item continua com o seu endereço e a sua guarda de área: a recepção vê
 * só o que pode abrir (Modelos e Minha conta, no padrão).
 */
const GRUPOS: Array<{ titulo: string; itens: Item[] }> = [
  {
    titulo: "A clínica",
    itens: [
      { rotulo: "Clínica e assistente", icone: "empresa", area: "empresa", to: "/empresa", secao: "clinica" },
      { rotulo: "Unidades e horários", icone: "agenda", area: "empresa", to: "/empresa", secao: "unidades" },
      { rotulo: "Profissionais", icone: "pacientes", area: "empresa", to: "/empresa", secao: "profissionais" },
      { rotulo: "Serviços", icone: "etiqueta", area: "empresa", to: "/empresa", secao: "servicos" },
      { rotulo: "Avisos automáticos", icone: "alerta", area: "empresa", to: "/empresa", secao: "avisos" },
      { rotulo: "Constituição", icone: "escudo", area: "empresa", to: "/empresa", secao: "constituicao" },
    ],
  },
  {
    titulo: "Comunicação",
    itens: [
      { rotulo: "Modelos de mensagem", icone: "modelos", area: "modelos", to: "/modelos" },
      { rotulo: "WhatsApp e Google", icone: "conexao", area: "conexao", to: "/conexao" },
    ],
  },
  {
    titulo: "Pessoas",
    itens: [
      { rotulo: "Equipe", icone: "equipe", area: "equipe", to: "/equipe" },
      { rotulo: "Minha conta", icone: "pessoa", area: "configuracoes", to: "/configuracoes" },
    ],
  },
];

/**
 * A moldura das telas de Ajustes: o menu de seções à esquerda (no celular, uma
 * faixa de pílulas que rola para o lado) e a página à direita, com título,
 * explicação e a ação principal (Salvar) no alto.
 */
export function Ajustes({
  titulo,
  descricao,
  acao,
  children,
}: {
  titulo: string;
  descricao?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
}) {
  const { data: eu } = useSuspenseQuery(euQuery());
  const local = useRouterState({ select: (r) => r.location });
  const secaoAtual = (local.search as { secao?: string }).secao ?? "clinica";
  const pode = new Set(eu.areas);
  const grupos = GRUPOS.map((g) => ({ ...g, itens: g.itens.filter((i) => pode.has(i.area)) })).filter((g) => g.itens.length > 0);

  return (
    <div className="flex h-full min-h-0 flex-col md:flex-row">
      <nav
        aria-label="Seções dos ajustes"
        className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-[var(--color-borda)] bg-[var(--color-cartao)] p-2.5 md:w-64 md:flex-col md:gap-0.5 md:overflow-y-auto md:border-r md:border-b-0 md:px-3 md:py-5"
      >
        <p className="mx-2 mb-2 hidden text-[22px] font-bold tracking-tight md:block">Ajustes</p>
        {grupos.map((g) => (
          <div key={g.titulo} className="contents">
            <p className="rotulo hidden px-2.5 pt-3.5 pb-1.5 md:block">{g.titulo}</p>
            {g.itens.map((i) => {
              const ativo = local.pathname === i.to && (!i.secao || i.secao === secaoAtual);
              return (
                <Link
                  key={i.rotulo}
                  to={i.to}
                  search={i.secao ? { secao: i.secao } : {}}
                  aria-current={ativo ? "page" : undefined}
                  className={
                    "flex h-[34px] shrink-0 items-center gap-2.5 rounded-full px-3 text-sm font-semibold whitespace-nowrap transition-colors md:h-[38px] md:rounded-[10px] " +
                    (ativo
                      ? "bg-[var(--color-realce)] text-[var(--color-marca-forte)]"
                      : "bg-[var(--color-painel)] text-[var(--color-suave)] hover:text-[var(--color-tinta)] md:bg-transparent md:hover:bg-[var(--color-painel)]")
                  }
                >
                  <Icone nome={i.icone} className="hidden h-4 w-4 md:block" />
                  {i.rotulo}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="anim-fade min-h-0 min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-[860px] flex-col gap-5 px-4 py-5 pb-14 md:px-8 md:py-7">
          <header className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-[22px] font-bold tracking-tight md:text-[26px]">{titulo}</h1>
              {descricao ? <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[var(--color-suave)]">{descricao}</p> : null}
            </div>
            {acao ? <div className="flex shrink-0 flex-wrap items-center gap-2">{acao}</div> : null}
          </header>
          {children}
        </div>
      </div>
    </div>
  );
}
